import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Table,
  Button,
  Row,
  Col,
  Spinner,
  Badge,
  Accordion,
  Form,
} from "react-bootstrap";
import Layout from "../../layout/Layout";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import { hatHizEslestirmeAnaliz } from "../../../api/planlama/hatHizStandart";

function HizPerformansRaporuPage() {
  const [data, setData] = useState([]);
  const [unmatchedData, setUnmatchedData] = useState([]);
  const [apiSummary, setApiSummary] = useState({});
  const [analizRaporu, setAnalizRaporu] = useState({
    makinaBazli: {},
    operatorBazli: {},
    vardiyaBazli: {},
    capBazli: {},
  });
  const [isLoading, setIsLoading] = useState(false);

  const [apiFilters, setApiFilters] = useState({
    yil: new Date().getFullYear(),
    ay: new Date().getMonth() + 1,
  });

  const [tableFilters, setTableFilters] = useState({
    makinaAdi: "",
    operator: "",
    vardiya: "",
  });

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await hatHizEslestirmeAnaliz(apiFilters.yil, apiFilters.ay);
      const responseData = res.data ? res.data : res;

      if (responseData) {
        setData(responseData.islenenler || []);
        setUnmatchedData(responseData.islenmeyenler || []);
        setApiSummary(responseData.genelOzet || {});
        setAnalizRaporu(
          responseData.analizRaporu || {
            makinaBazli: {},
            operatorBazli: {},
            vardiyaBazli: {},
            capBazli: {},
          }
        );
      } else {
        setData([]);
        setUnmatchedData([]);
      }
    } catch (error) {
      console.error("Veri çekme hatası:", error);
      setData([]);
      setUnmatchedData([]);
    } finally {
      setIsLoading(false);
    }
  }, [apiFilters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const applyClientFilters = (dataset) => {
    return dataset.filter((item) => {
      let isValid = true;
      if (
        tableFilters.makinaAdi &&
        String(item.makinaAdi) !== String(tableFilters.makinaAdi)
      )
        isValid = false;
      if (
        tableFilters.operator &&
        !item.operator
          .toLowerCase()
          .includes(tableFilters.operator.toLowerCase())
      )
        isValid = false;
      if (
        tableFilters.vardiya &&
        String(item.vardiya) !== String(tableFilters.vardiya)
      )
        isValid = false;
      return isValid;
    });
  };

  const filteredData = applyClientFilters(data);
  const filteredUnmatchedData = applyClientFilters(unmatchedData);

  const handleTableFilterChange = (key, value) => {
    setTableFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearTableFilters = () => {
    setTableFilters({ makinaAdi: "", operator: "", vardiya: "" });
  };

  // --- HESAPLAMALAR VE GRAFİK VERİLERİ ---
  let toplamAltinda = 0;
  let toplamUstunde = 0;
  let toplamTamHedefte = 0;

  Object.values(analizRaporu.makinaBazli || {}).forEach((m) => {
    toplamAltinda += m.hedefiTutturamayanSayisi || 0;
    toplamUstunde += m.hedefiGecenSayisi || 0;
    toplamTamHedefte += m.tamHedefteSayisi || 0;
  });

  const pieData = [
    { name: "Hedefin Altında (Başarısız)", value: toplamAltinda },
    { name: "Tam Hedefte", value: toplamTamHedefte },
    { name: "Hedefin Üstünde (Başarılı)", value: toplamUstunde },
  ];
  const pieColors = ["#dc3545", "#ffc107", "#198754"];

  const formatChartData = (dataObj) => {
    if (!dataObj) return [];
    return Object.keys(dataObj).map((key) => ({
      name: key,
      "Hedefin Altında": dataObj[key].hedefiTutturamayanSayisi || 0,
      "Hedefin Üstünde": dataObj[key].hedefiGecenSayisi || 0,
      "Eksik Veri": dataObj[key].hedefHizEksikSayisi || 0,
    }));
  };

  const makineBarData = formatChartData(analizRaporu.makinaBazli);
  const operatorBarData = formatChartData(analizRaporu.operatorBazli);
  const vardiyaBarData = formatChartData(analizRaporu.vardiyaBazli);
  const capBarData = formatChartData(analizRaporu.capBazli);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border rounded p-2 shadow-sm">
          <p className="m-0 fw-bold border-bottom pb-1 mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="m-0" style={{ color: entry.color }}>
              {entry.name}: {entry.value} Adet
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <Layout>
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
        <div>
          <h3 className="fw-bold text-dark m-0">
            Üretim Hız Performans Analizi
          </h3>
          <small className="text-muted">
            Makine, operatör, vardiya ve ebat bazlı hedef hız ve gerçekleşen hız
            karşılaştırmaları.
          </small>
        </div>

        <div className="d-flex gap-2 align-items-center">
          <small className="fw-bold text-muted">Dönem:</small>
          <Form.Select
            size="sm"
            value={apiFilters.ay}
            onChange={(e) =>
              setApiFilters({ ...apiFilters, ay: e.target.value })
            }
            className="shadow-sm"
          >
            {[...Array(12).keys()].map((i) => (
              <option key={i + 1} value={i + 1}>
                {i + 1}. Ay
              </option>
            ))}
          </Form.Select>
          <Form.Select
            size="sm"
            value={apiFilters.yil}
            onChange={(e) =>
              setApiFilters({ ...apiFilters, yil: e.target.value })
            }
            className="shadow-sm"
          >
            {[2025, 2026, 2027].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </Form.Select>
        </div>
      </div>

      <Row className="mb-4 g-3">
        <Col md={3}>
          <Card className="border-0 shadow-sm h-100 border-start border-primary border-4">
            <Card.Body>
              <h6 className="text-muted mb-2">Analiz Edilen Üretim</h6>
              <h3 className="fw-bold text-dark m-0">
                {apiSummary.islenenKayitSayisi || 0}{" "}
                <span className="fs-6 fw-normal text-muted">
                  / {apiSummary.toplamSorgulananKayit || 0}
                </span>
              </h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm h-100 border-start border-danger border-4">
            <Card.Body>
              <h6 className="text-muted mb-2">Hedefin Altında Kalanlar</h6>
              <h3 className="fw-bold text-danger m-0">
                {toplamAltinda}{" "}
                <small className="fs-6 fw-normal text-muted">Adet</small>
              </h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm h-100 border-start border-success border-4">
            <Card.Body>
              <h6 className="text-muted mb-2">Hedefin Üstünde Çıkanlar</h6>
              <h3 className="fw-bold text-success m-0">
                {toplamUstunde}{" "}
                <small className="fs-6 fw-normal text-muted">Adet</small>
              </h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm h-100 border-start border-warning border-4">
            <Card.Body>
              <h6 className="text-muted mb-2">Tam Hedefte Olanlar</h6>
              <h3 className="fw-bold text-warning m-0">
                {toplamTamHedefte}{" "}
                <small className="fs-6 fw-normal text-muted">Adet</small>
              </h3>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {!isLoading && filteredData.length > 0 && (
        <>
          <Row className="mb-4 g-3">
            <Col md={4}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Header className="bg-white border-0 pt-3">
                  <h6 className="fw-bold m-0 text-secondary">
                    Genel Hız Başarı Oranı
                  </h6>
                </Card.Header>
                <Card.Body className="d-flex align-items-center justify-content-center">
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {pieData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={pieColors[index % pieColors.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                      <Legend verticalAlign="bottom" height={36} />
                    </PieChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Header className="bg-white border-0 pt-3">
                  <h6 className="fw-bold m-0 text-secondary">
                    Makine Bazlı Performans Analizi
                  </h6>
                </Card.Header>
                <Card.Body>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart
                      data={makineBarData}
                      margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" fontSize={12} />
                      <YAxis fontSize={12} allowDecimals={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar
                        dataKey="Hedefin Altında"
                        fill="#dc3545"
                        radius={[4, 4, 0, 0]}
                        barSize={20}
                      />
                      <Bar
                        dataKey="Hedefin Üstünde"
                        fill="#198754"
                        radius={[4, 4, 0, 0]}
                        barSize={20}
                      />
                      <Bar
                        dataKey="Eksik Veri"
                        fill="#6c757d"
                        radius={[4, 4, 0, 0]}
                        barSize={20}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Header className="bg-white border-0 pt-3">
                  <h6 className="fw-bold m-0 text-secondary">
                    Vardiya Bazlı Performans Analizi
                  </h6>
                </Card.Header>
                <Card.Body>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart
                      data={vardiyaBarData}
                      margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" fontSize={12} />
                      <YAxis fontSize={12} allowDecimals={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar
                        dataKey="Hedefin Altında"
                        fill="#dc3545"
                        radius={[4, 4, 0, 0]}
                        barSize={20}
                      />
                      <Bar
                        dataKey="Hedefin Üstünde"
                        fill="#198754"
                        radius={[4, 4, 0, 0]}
                        barSize={20}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="mb-4 g-3">
            <Col md={6}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Header className="bg-white border-0 pt-3">
                  <h6 className="fw-bold m-0 text-secondary">
                    Operatör Bazlı Performans Analizi
                  </h6>
                </Card.Header>
                <Card.Body>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart
                      data={operatorBarData}
                      margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" fontSize={12} />
                      <YAxis fontSize={12} allowDecimals={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar
                        dataKey="Hedefin Altında"
                        fill="#dc3545"
                        radius={[4, 4, 0, 0]}
                        barSize={20}
                      />
                      <Bar
                        dataKey="Hedefin Üstünde"
                        fill="#198754"
                        radius={[4, 4, 0, 0]}
                        barSize={20}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Header className="bg-white border-0 pt-3">
                  <h6 className="fw-bold m-0 text-secondary">
                    Çap (Ebat) Bazlı Uyuşmazlık Analizi
                  </h6>
                </Card.Header>
                <Card.Body>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart
                      data={capBarData}
                      margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis
                        dataKey="name"
                        fontSize={10}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis fontSize={12} allowDecimals={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend verticalAlign="top" />
                      <Bar
                        dataKey="Hedefin Altında"
                        fill="#dc3545"
                        radius={[4, 4, 0, 0]}
                        barSize={15}
                      />
                      <Bar
                        dataKey="Hedefin Üstünde"
                        fill="#198754"
                        radius={[4, 4, 0, 0]}
                        barSize={15}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}

      <Accordion className="mb-4 shadow-sm" defaultActiveKey="0">
        <Accordion.Item eventKey="0" className="border-0">
          <Accordion.Header>
            <span className="fw-bold text-secondary">
              Detaylı Tablo Filtresi
            </span>
          </Accordion.Header>
          <Accordion.Body className="bg-light">
            <Row className="g-3">
              <Col md={4}>
                <Form.Label className="small fw-bold text-muted">
                  Makine Hattı
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={tableFilters.makinaAdi}
                  onChange={(e) =>
                    handleTableFilterChange("makinaAdi", e.target.value)
                  }
                >
                  <option value="">Tümü</option>
                  {[1, 2, 3, 4].map((n) => (
                    <option key={n} value={n}>
                      {n}. Makine
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={4}>
                <Form.Label className="small fw-bold text-muted">
                  Operatör Adı
                </Form.Label>
                <Form.Control
                  size="sm"
                  placeholder="Örn: Ali"
                  value={tableFilters.operator}
                  onChange={(e) =>
                    handleTableFilterChange("operator", e.target.value)
                  }
                />
              </Col>
              <Col md={4}>
                <Form.Label className="small fw-bold text-muted">
                  Vardiya
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={tableFilters.vardiya}
                  onChange={(e) =>
                    handleTableFilterChange("vardiya", e.target.value)
                  }
                >
                  <option value="">Tümü</option>
                  <option value="1">1. Vardiya</option>
                  <option value="2">2. Vardiya</option>
                  <option value="3">3. Vardiya</option>
                </Form.Select>
              </Col>
              <Col md={12} className="text-end border-top pt-2">
                <Button
                  variant="link"
                  size="sm"
                  className="text-danger text-decoration-none"
                  onClick={clearTableFilters}
                >
                  Filtreleri Temizle
                </Button>
              </Col>
            </Row>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <h5 className="fw-bold text-dark mb-3">Analiz Edilen Üretimler</h5>
      <Card className="shadow-sm border-0 mb-5">
        <div className="table-responsive">
          <Table
            hover
            striped
            bordered
            size="sm"
            className="mb-0 align-middle text-center text-nowrap"
            style={{ fontSize: "12px" }}
          >
            <thead className="table-dark align-middle">
              <tr>
                <th rowSpan="2" className="py-2">
                  Tarih
                </th>
                <th rowSpan="2" className="py-2">
                  Makine
                </th>
                <th rowSpan="2" className="py-2">
                  Vardiya
                </th>
                <th rowSpan="2" className="py-2 text-start">
                  Operatör
                </th>
                <th rowSpan="2" className="py-2 text-start">
                  Firma
                </th>
                <th
                  colSpan="6"
                  className="py-1 bg-secondary border-bottom border-dark"
                >
                  Ürün & Kalıp Detayları
                </th>
                <th
                  colSpan="4"
                  className="py-1 bg-primary border-bottom border-dark"
                >
                  Hız Analizi (m/dk)
                </th>
              </tr>
              <tr>
                <th className="py-1 bg-secondary text-white">Malzeme</th>
                <th className="py-1 bg-secondary text-white">Ebat</th>
                <th className="py-1 bg-secondary text-white">Kalınlık</th>
                <th className="py-1 bg-secondary text-white">Bant Eni</th>
                <th className="py-1 bg-secondary text-white">Boy</th>
                <th className="py-1 bg-secondary text-white">Paket İçi</th>
                <th className="py-1 bg-primary text-white">Hedef</th>
                <th className="py-1 bg-primary text-white">Gerçek</th>
                <th className="py-1 bg-primary text-white">Fark</th>
                <th className="py-1 bg-primary text-white">Durum</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="15" className="py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">Veriler hesaplanıyor...</p>
                  </td>
                </tr>
              ) : filteredData.length > 0 ? (
                filteredData.map((row) => (
                  <tr key={row._id}>
                    <td>{new Date(row.tarih).toLocaleDateString("tr-TR")}</td>
                    <td className="fw-bold">{row.makinaAdi}</td>
                    <td>{row.vardiya}</td>
                    <td className="text-start text-capitalize">
                      {row.operator}
                    </td>
                    <td
                      className="text-start text-truncate"
                      style={{ maxWidth: "150px" }}
                      title={row.firma}
                    >
                      {row.firma}
                    </td>
                    <td className="text-uppercase fw-semibold">
                      {row.malzemeCinsi}
                    </td>
                    <td className="fw-bold text-dark">{row.ebat}</td>
                    <td>{row.kalinlik} mm</td>
                    <td>{row.bantEni} mm</td>
                    <td>{row.boy} mm</td>
                    <td className="fw-bold text-primary">{row.paketIciAdet}</td>
                    <td className="fw-bold text-secondary">{row.hedefHiz}</td>
                    <td
                      className={`fw-bold ${
                        row.hizFarki >= 0 ? "text-success" : "text-danger"
                      }`}
                    >
                      {row.gerceklesenHiz}
                    </td>
                    <td>
                      <Badge
                        bg={
                          row.hizFarki >= 0
                            ? row.hizFarki === 0
                              ? "warning"
                              : "success"
                            : "danger"
                        }
                      >
                        {row.hizFarki > 0 ? `+${row.hizFarki}` : row.hizFarki}
                      </Badge>
                    </td>
                    <td
                      className={`fw-bold ${
                        row.hizFarki >= 0
                          ? row.hizFarki === 0
                            ? "text-warning"
                            : "text-success"
                          : "text-danger"
                      }`}
                    >
                      {row.durum}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="15" className="py-4 text-muted">
                    Belirtilen kriterlerde analiz edilecek veri bulunamadı.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Card>

      <div className="d-flex align-items-center mb-3">
        <h5 className="fw-bold text-danger m-0">
          Hedef Hız Verisi Eksik Olan Kayıtlar
        </h5>
        <Badge bg="danger" className="ms-3 fs-6 rounded-pill">
          {filteredUnmatchedData.length} Kayıt
        </Badge>
      </div>
      <p className="text-muted small mb-3">
        Aşağıdaki üretim verilerinde hedef hız (planlanan hız) değeri
        girilmediği için performans analizine dahil edilememiştir. Lütfen üretim
        kayıtlarını kontrol ederek eksik verileri tamamlayınız.
      </p>

      <Card className="shadow-sm border-0 border-top border-danger border-4">
        <div className="table-responsive">
          <Table
            hover
            striped
            bordered
            size="sm"
            className="mb-0 align-middle text-center text-nowrap"
            style={{ fontSize: "12px" }}
          >
            <thead className="table-light align-middle text-muted">
              <tr>
                <th className="py-2">Tarih</th>
                <th className="py-2">Makine</th>
                <th className="py-2">Vardiya</th>
                <th className="py-2 text-start">Operatör</th>
                <th className="py-2 text-start">Firma</th>
                <th className="py-2">Ebat</th>
                <th className="py-2">Kalınlık</th>
                <th className="py-2">Bant Eni</th>
                <th className="py-2">Boy</th>
                <th className="py-2">Paket İçi</th>
                <th className="py-2 text-primary">Gerçekleşen Hız</th>
                <th className="py-2 text-danger">Sebep</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="12" className="py-4">
                    <Spinner animation="border" variant="secondary" size="sm" />
                  </td>
                </tr>
              ) : filteredUnmatchedData.length > 0 ? (
                filteredUnmatchedData.map((row) => (
                  <tr key={row._id}>
                    <td>{new Date(row.tarih).toLocaleDateString("tr-TR")}</td>
                    <td className="fw-bold">{row.makinaAdi}</td>
                    <td>{row.vardiya}</td>
                    <td className="text-start">{row.operator}</td>
                    <td
                      className="text-start text-truncate"
                      style={{ maxWidth: "120px" }}
                      title={row.firma}
                    >
                      {row.firma}
                    </td>
                    <td className="fw-bold text-dark">{row.ebat}</td>
                    <td className="text-danger fw-semibold">
                      {row.kalinlik} mm
                    </td>
                    <td className="text-danger fw-semibold">
                      {row.bantEni} mm
                    </td>
                    <td className="text-danger fw-semibold">{row.boy} mm</td>
                    <td className="text-danger fw-semibold">
                      {row.paketIciAdet}
                    </td>
                    <td className="fw-bold text-primary">
                      {row.gerceklesenHiz} m/dk
                    </td>
                    <td className="text-danger small">{row.sebep}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="12" className="py-4 text-muted">
                    Bu kriterlere ait eksik veri barındıran kayıt bulunmuyor.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Card>
    </Layout>
  );
}

export default HizPerformansRaporuPage;
